import { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Link, Paragraph } from '@digdir/designsystemet-react';
import { useGetUserinfo } from '../../hooks/queries/useGetUserinfo';
import { startPageFor } from './startPage';
import layout from '../../pages/HRDashboard/hmsPage.module.css';

export type PageProblemKind = 'noAccess' | 'notFound';

type Props = {
  kind: PageProblemKind;
  /** What was not found, such as «Vi fant ikke vernerunden». The general text is used without it. */
  title?: string;
  message?: string;
};

/**
 * The page cannot be shown: the user lacks access, or it does not exist. Says so plainly, and leads back to where the
 * user's own part of the app starts.
 */
export const PageProblem = ({ kind, title, message }: Props): ReactElement => {
  const { t } = useTranslation();
  const { data: userInfo } = useGetUserinfo();
  const roles = userInfo?.userRoles ?? [];
  return (
    <div className={layout.loading}>
      <Alert data-color={kind === 'noAccess' ? 'warning' : 'info'}>
        <Heading level={1} data-size="2xs">
          {title ?? t(`pageProblem.${kind}.title`)}
        </Heading>
        <Paragraph>{message ?? t(`pageProblem.${kind}.message`)}</Paragraph>
        <Paragraph>
          <Link asChild>
            <RouterLink to={startPageFor(roles, userInfo?.leadsTeam)}>{t('pageProblem.home')}</RouterLink>
          </Link>
        </Paragraph>
      </Alert>
    </div>
  );
};

/** For an address that matches no page */
export const NotFoundPage = (): ReactElement => <PageProblem kind="notFound" />;
